import { useDeviceType } from '@cian/ui-kit';
import RBush from 'rbush';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Desktop } from 'shared/common/packages/Responsive';
import { useApplicationContext } from 'shared/map-search/containers/ApplicationContext';
import { selectClustersType } from 'shared/map-search/selectors/clustersType';
import { IRBushItem } from 'shared/map-search/types/map';
import { useDailyRentAvailabilityFeatureEnabled } from 'shared/map-search/utils/dailyRentAvailability';

import type { IApplicationState } from 'shared/map-search/types/redux';
import type { TPromoPinConfig } from 'shared/map-search/utils/ymapsModules/PinPulseManager';

import { ClickHandlers } from './ClickHandlers';
import { DetailsVisibilityManager } from './DetailsVisibilityManager';
import { DetailsVisibilityPromoManager } from './DetailsVisibilityPromoManager';
import { DotActive } from './DotActive';
import { DotHover } from './DotHover';
import { FavoritesUpdater } from './FavoritesUpdater';
import { FeaturesPropertiesUpdater } from './FeaturesPropertiesUpdater';
import { FeaturesUpdater } from './FeaturesUpdater';
import { NewbuildingPromoPinsManager } from './NewbuildingPromoPinsManager';
import { NewbuildingPromoPopupManager } from './NewbuildingPromoPopupManager';
import { NotFoundContainer } from './NotFoundContainer';
import { OffersCounter } from './OffersCounter';
import { PopupManager } from './PopupManager';
import { ResultabilityManager } from './ResultabilityManager';
import { SubdomainUpdater } from './SubdomainUpdater';
import { TestMode } from './TestMode';
import { FeaturesContext, IFeaturesContext } from './context';
import { useMapContext } from '../../context';

export interface IFeaturesApi {
  init(): void;

  destroy(): void;
}

export const Features = React.forwardRef((_, ref) => {
  const { config } = useApplicationContext();
  const { ymaps, map } = useMapContext();
  /**
   * @todo разработка в MNZ-700
   * Получаем конфигурацию пинов промо-слоя
   */
  const promoPinConfig = useSelector<IApplicationState, TPromoPinConfig>(state => state.promoPinConfig);

  const clustersType = useSelector(selectClustersType);

  // @todo выпилить эксп CD-159979
  const showOnlyPrice = useDailyRentAvailabilityFeatureEnabled();

  const deviceType = useDeviceType();
  const isMobileView = deviceType === 'phone';

  const rbushRef = React.useRef(new RBush<IRBushItem>());
  const pointsLayerRef = React.useRef(
    new ymaps.PointsLayer({
      map,
      rbush: rbushRef.current,
      promoPinConfig,
      pointType: clustersType,
      isMobileView,
    }),
  );

  const hotspotsLayerRef = React.useRef(
    new ymaps.HotspotsLayer({
      map,
      rbush: rbushRef.current,
      pointType: clustersType,
    }),
  );
  const balloonManagerRef = React.useRef(
    new ymaps.BalloonManager({
      map,
      rbush: rbushRef.current,
      showOnlyPrice,
      pointType: clustersType,
    }),
  );
  const balloonPromoManagerRef = React.useRef(
    new ymaps.BalloonPromoManager({
      map,
      rbush: rbushRef.current,
      pointType: clustersType,
    }),
  );
  const popupManagerRef = React.useRef(new ymaps.PopupManager({ map }));
  const newbuildingPopupManagerRef = React.useRef(new ymaps.NewbuildingPromoPopupManager({ map }));
  const [isDetailsVisible, changeIsDetailsVisible] = React.useState(false);
  const [isPromoDetailsVisible, changeIsPromoDetailsVisible] = React.useState(false);
  const [isResultsAvailable, changeIsResultsAvailable] = React.useState(false);
  const isPopupEnabled = config.get<boolean>('pinPopup.enabled');

  const handleVisibilityChange = React.useCallback((isDetailsVisible: boolean) => {
    changeIsDetailsVisible(isDetailsVisible);
  }, []);

  const handleResultabilityChange = React.useCallback((isResultable: boolean) => {
    changeIsResultsAvailable(isResultable);
  }, []);

  const handlePromoVisibilityChange = React.useCallback((isVisible: boolean) => {
    changeIsPromoDetailsVisible(isVisible);
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({
      init: () => {
        map.layers.add(pointsLayerRef.current.layer);
        map.layers.add(hotspotsLayerRef.current.layer);
        map.geoObjects.add(balloonManagerRef.current.objectManager);
        map.geoObjects.add(balloonPromoManagerRef.current.objectManager);
      },
      destroy: () => {
        map.layers.remove(pointsLayerRef.current.layer);
        map.layers.remove(hotspotsLayerRef.current.layer);
        map.geoObjects.remove(balloonManagerRef.current.objectManager);
        map.geoObjects.remove(balloonPromoManagerRef.current.objectManager);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const context = React.useMemo<IFeaturesContext>(
    () => ({
      rbush: rbushRef.current,
      pointsLayer: pointsLayerRef.current,
      hotspotsLayer: hotspotsLayerRef.current,
      balloonManager: balloonManagerRef.current,
      balloonPromoManager: balloonPromoManagerRef.current,
      popupManager: popupManagerRef.current,
      newbuildingPopupManager: newbuildingPopupManagerRef.current,
    }),
    [],
  );

  return (
    <FeaturesContext.Provider value={context}>
      <ClickHandlers
        resultsAvailable={isResultsAvailable}
        detailsVisible={isDetailsVisible}
        promoDetailsVisible={isPromoDetailsVisible}
      />
      <DetailsVisibilityManager onVisibilityChange={handleVisibilityChange} />
      <DetailsVisibilityPromoManager onPromoVisibilityChange={handlePromoVisibilityChange} />
      <Desktop>
        {isPopupEnabled && <PopupManager resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />}
        <NewbuildingPromoPopupManager resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      </Desktop>
      <DotActive resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <DotHover resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <FavoritesUpdater promoDetailsVisible={isPromoDetailsVisible} />
      <FeaturesPropertiesUpdater promoPinConfig={promoPinConfig} promoDetailsVisible={isPromoDetailsVisible} />
      <FeaturesUpdater
        detailsVisible={isDetailsVisible || showOnlyPrice}
        promoPinConfig={promoPinConfig}
        promoDetailsVisible={isPromoDetailsVisible}
      />
      <NewbuildingPromoPinsManager detailsVisible={isDetailsVisible} />
      <NotFoundContainer />
      <ResultabilityManager onResultabilityChange={handleResultabilityChange} />
      <SubdomainUpdater />
      <TestMode map={map} />
      <OffersCounter />
    </FeaturesContext.Provider>
  );
});

Features.displayName = 'Features';
